import { z } from 'zod';
import { API_TOKEN_ACCESS, type ApiTokenAccess } from '../enums';
import { MCP_PATH } from './mcp';
import { personalTokenScopeInput } from './tokens';

/**
 * Serwer autoryzacji OAuth 2.1 dla MCP — „konektor Claude” (DECISIONS 2026-10-09 [S], docs/MCP_TOOLS.md §4.3,
 * docs/API_AUTH.md „Konektor”). Fravity OS jest jednocześnie serwerem zasobu (`/mcp`) i serwerem autoryzacji:
 *  - metadane zasobu (RFC 9728) `/.well-known/oauth-protected-resource[/mcp]` i serwera (RFC 8414)
 *    `/.well-known/oauth-authorization-server`,
 *  - dynamiczna rejestracja klienta publicznego (RFC 7591) `POST /oauth/register` — bez sekretów klienta,
 *  - kod autoryzacji z PKCE S256 (obowiązkowo) `GET /oauth/authorize` → ekran zgody w aplikacji (`/polacz`),
 *  - `POST /oauth/token` (kod jednorazowy 60 s; odświeżanie z rotacją), `POST /oauth/revoke` (RFC 7009),
 *  - wskaźnik zasobu (RFC 8707): jedyny zasób = `APP_ORIGIN/mcp`; token jest z nim związany.
 * Token dostępu działa na `/mcp` jak token osobisty osoby, która wyraziła zgodę (`api_tokens.kind = 'oauth'`).
 */

// ── Adresy ──────────────────────────────────────────────────────────────────

export const OAUTH_PATHS = {
  authorize: '/oauth/authorize',
  token: '/oauth/token',
  register: '/oauth/register',
  revoke: '/oauth/revoke',
} as const;

export const OAUTH_WELL_KNOWN = {
  /** RFC 9728 §3.1: dla zasobu ze ścieżką (`/mcp`) adres metadanych ma ścieżkę zasobu na końcu; serwujemy oba */
  protectedResource: '/.well-known/oauth-protected-resource',
  authorizationServer: '/.well-known/oauth-authorization-server',
} as const;

/** Ekran zgody w SPA (pod strażnikiem sesji: bez logowania → /login?next=… → powrót). */
export const OAUTH_CONSENT_WEB_PATH = '/polacz';

/** Adresy powrotu Claude (claude.ai, aplikacja mobilna, Desktop; docs Anthropic „Authentication for connectors”). */
export const CLAUDE_CALLBACK_URIS = [
  'https://claude.ai/api/mcp/auth_callback',
  'https://claude.com/api/mcp/auth_callback',
] as const;

/** Wystawca (issuer) = adres aplikacji bez końcowego `/` (RFC 8414 §2). */
export function oauthIssuer(appOrigin: string): string {
  return appOrigin.replace(/\/+$/, '');
}

/** Kanoniczny adres serwera MCP (RFC 8707 / RFC 9728 `resource`): `https://os.fravity.pl/mcp`. */
export function mcpResourceUri(appOrigin: string): string {
  return `${oauthIssuer(appOrigin)}${MCP_PATH}`;
}

/** Adres metadanych zasobu wskazywany w `WWW-Authenticate: Bearer resource_metadata="…"` (401 z `/mcp`). */
export function protectedResourceMetadataUrl(appOrigin: string): string {
  return `${oauthIssuer(appOrigin)}${OAUTH_WELL_KNOWN.protectedResource}${MCP_PATH}`;
}

// ── Zakresy, ważność, formaty ───────────────────────────────────────────────

export const OAUTH_SCOPES = ['mcp:read', 'mcp:write'] as const;
export type OauthScope = (typeof OAUTH_SCOPES)[number];

/** Kod autoryzacji: jednorazowy, krótko ważny. */
export const OAUTH_CODE_TTL_SEC = 60;
/** Token dostępu: krótki (Claude odświeża go sam, reaktywnie po 401 i do 5 min przed końcem). */
export const OAUTH_ACCESS_TOKEN_TTL_SEC = 3600;
/** Token odświeżania: każde odświeżenie daje nowy (rotacja) na kolejne 90 dni, najdłużej do końca ważności połączenia. */
export const OAUTH_REFRESH_TOKEN_TTL_SEC = 90 * 86_400;
/**
 * Okno tolerancji dla właśnie zrotowanego tokenu odświeżania (wyścig równoległych odświeżeń, ponowienie po zgubionej
 * odpowiedzi): przez tyle sekund od pierwszego użycia ten sam token daje nową parę dla tego samego połączenia. Później
 * ponowne użycie = cofnięcie całego połączenia (rodziny tokenów).
 */
export const OAUTH_REFRESH_REUSE_GRACE_SEC = 60;
/** Ważność połączenia (zgody) do wyboru na ekranie zgody; po niej Claude poprosi o ponowne połączenie. */
export const OAUTH_CONNECTION_EXPIRY_DAYS = [30, 90, 365] as const;
export const OAUTH_CONNECTION_DEFAULT_EXPIRY_DAYS = 90;
/** Najwyżej tyle aktywnych połączeń konektorów na osobę (każde połączenie Claude = osobny wpis). */
export const OAUTH_MAX_ACTIVE_CONNECTIONS = 20;

export const OAUTH_ACCESS_TOKEN_PREFIX = 'fos_oat_';
export const OAUTH_REFRESH_TOKEN_PREFIX = 'fos_ort_';
export const OAUTH_CLIENT_ID_PREFIX = 'fos_cli_';
const OAUTH_REFRESH_RE = /^fos_ort_[A-Za-z0-9_-]{43}$/;
const OAUTH_ACCESS_RE = /^fos_oat_[A-Za-z0-9_-]{43}$/;

export function isOauthRefreshToken(raw: string): boolean {
  return OAUTH_REFRESH_RE.test(raw);
}
export function isOauthAccessToken(raw: string): boolean {
  return OAUTH_ACCESS_RE.test(raw);
}

/** RFC 7636 §4.2: S256 = BASE64URL(SHA-256(weryfikator)) = 43 znaki. */
export const PKCE_CHALLENGE_RE = /^[A-Za-z0-9_-]{43}$/;
/** RFC 7636 §4.1: 43–128 znaków z alfabetu „unreserved”. */
export const PKCE_VERIFIER_RE = /^[A-Za-z0-9._~-]{43,128}$/;

export const OAUTH_STATE_MAX = 2048;
export const OAUTH_MAX_REDIRECT_URIS = 10;
export const OAUTH_REDIRECT_URI_MAX = 2000;
export const OAUTH_CLIENT_NAME_MAX = 100;

/**
 * Najwyższy poziom, o który prosi klient (`scope`): bez `scope` albo z `mcp:write` → zapis; tylko `mcp:read` → odczyt.
 * Nieznane zakresy (np. `offline_access`, `openid`) pomijamy — o dostępie decyduje osoba na ekranie zgody.
 */
export function requestedMaxAccess(scope: string | null | undefined): ApiTokenAccess {
  const parts = (scope ?? '').split(/\s+/).filter(Boolean);
  if (parts.length === 0 || parts.includes('mcp:write')) return 'write';
  return parts.includes('mcp:read') ? 'read' : 'write';
}

/** Zakres w odpowiedzi tokenu: `mcp:read` albo `mcp:read mcp:write`. */
export function grantedScope(access: ApiTokenAccess): string {
  return access === 'write' ? 'mcp:read mcp:write' : 'mcp:read';
}

// ── Adresy powrotu i zasób ──────────────────────────────────────────────────

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

function parseUrl(raw: string): URL | null {
  try {
    return new URL(raw);
  } catch {
    return null;
  }
}

function isLoopbackUrl(u: URL): boolean {
  return u.protocol === 'http:' && LOOPBACK_HOSTS.has(u.hostname);
}

/**
 * Reguła adresu powrotu przy rejestracji: `https://…` albo `http://localhost|127.0.0.1|[::1]` (aplikacje lokalne, np.
 * Claude Code). Bez fragmentu (`#`) i bez danych logowania w adresie. `null` = poprawny.
 */
export function redirectUriProblem(raw: string): string | null {
  if (raw.length === 0 || raw.length > OAUTH_REDIRECT_URI_MAX) return OAUTH_MESSAGES.redirectInvalid;
  const u = parseUrl(raw);
  if (!u) return OAUTH_MESSAGES.redirectInvalid;
  if (u.hash || raw.includes('#')) return OAUTH_MESSAGES.redirectFragment;
  if (u.username || u.password) return OAUTH_MESSAGES.redirectInvalid;
  if (u.protocol === 'https:' && u.hostname) return null;
  if (isLoopbackUrl(u)) return null;
  return OAUTH_MESSAGES.redirectScheme;
}

/**
 * Adres powrotu z żądania zgadza się z zarejestrowanym: dokładnie ten sam napis. Wyjątek (RFC 8252 §7.3, Claude Code):
 * adres pętli zwrotnej `http://127.0.0.1|localhost|[::1]` — port może być inny (efemeryczny), reszta identyczna.
 */
export function redirectUriMatches(registered: readonly string[], requested: string): boolean {
  if (registered.includes(requested)) return true;
  const req = parseUrl(requested);
  if (!req || !isLoopbackUrl(req) || req.hash || req.username || req.password) return false;
  return registered.some((r) => {
    const u = parseUrl(r);
    return (
      !!u && isLoopbackUrl(u) && u.hostname === req.hostname && u.pathname === req.pathname && u.search === req.search
    );
  });
}

/** Rodzaj klienta na ekranie zgody: Claude (znany adres powrotu), aplikacja lokalna albo inna. */
export type OauthClientKind = 'claude' | 'local' | 'other';

export function oauthClientKind(redirectUri: string): OauthClientKind {
  if ((CLAUDE_CALLBACK_URIS as readonly string[]).includes(redirectUri)) return 'claude';
  const u = parseUrl(redirectUri);
  return u && isLoopbackUrl(u) ? 'local' : 'other';
}

/** Host adresu powrotu (pokazywany wyraźnie na ekranie zgody — wymóg specyfikacji MCP). */
export function redirectHost(redirectUri: string): string {
  return parseUrl(redirectUri)?.host ?? redirectUri;
}

/**
 * Kanoniczna postać `resource` (RFC 8707 §2, MCP „Canonical Server URI”): schemat i host małymi literami (robi to
 * parser URL), bez końcowego `/`, bez fragmentu i zapytania. `null` = nieprawidłowy adres.
 */
export function canonicalResource(raw: string): string | null {
  const u = parseUrl(raw.trim());
  if (!u || raw.includes('#') || u.search || u.username || u.password) return null;
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  return `${u.protocol}//${u.host}${u.pathname.replace(/\/+$/, '')}`;
}

// ── Wejścia ─────────────────────────────────────────────────────────────────

/** Parametry żądania autoryzacji (z adresu `GET /oauth/authorize`, przekazywane do ekranu zgody bez zmian). */
export const oauthAuthorizeParams = z
  .object({
    response_type: z.string().max(64).optional(),
    client_id: z.string().max(200).optional(),
    redirect_uri: z.string().max(4096).optional(),
    code_challenge: z.string().max(256).optional(),
    code_challenge_method: z.string().max(32).optional(),
    state: z.string().max(4096).optional(),
    scope: z.string().max(1000).optional(),
    resource: z.string().max(4096).optional(),
  })
  .strip();
export type OauthAuthorizeParams = z.infer<typeof oauthAuthorizeParams>;

/** RFC 7591 §2 (podzbiór; pozostałe pola metadanych ignorujemy, RFC 7591 §3.2.1). */
export const oauthRegisterInput = z
  .object({
    redirect_uris: z.array(z.string()).min(1).max(OAUTH_MAX_REDIRECT_URIS),
    client_name: z.string().max(500).optional(),
    token_endpoint_auth_method: z.string().max(64).optional(),
    grant_types: z.array(z.string().max(64)).max(10).optional(),
    response_types: z.array(z.string().max(64)).max(10).optional(),
    scope: z.string().max(1000).optional(),
  })
  .passthrough();

/** Ekran zgody → „Zezwól” (step-up hasłem jak przy „Nowy token”; CSRF jak każda mutacja tRPC). */
export const oauthApproveInput = z
  .object({
    request: oauthAuthorizeParams,
    access: z.enum(API_TOKEN_ACCESS),
    scope: personalTokenScopeInput,
    expiresInDays: z
      .number()
      .int()
      .refine((d) => (OAUTH_CONNECTION_EXPIRY_DAYS as readonly number[]).includes(d), {
        message: 'Wybierz ważność: 30, 90 albo 365 dni',
      })
      .default(OAUTH_CONNECTION_DEFAULT_EXPIRY_DAYS),
  })
  .strict();
export type OauthApproveInput = z.input<typeof oauthApproveInput>;

export const oauthDenyInput = z.object({ request: oauthAuthorizeParams }).strict();
export const oauthConsentInfoInput = z.object({ request: oauthAuthorizeParams }).strict();

// ── Wyniki ──────────────────────────────────────────────────────────────────

export interface OauthConsentInfo {
  client: {
    clientId: string;
    /** nazwa podana przez klienta przy rejestracji (niezweryfikowana) */
    name: string;
    kind: OauthClientKind;
    redirectHost: string;
  };
  /** najwyższy poziom, o który prosi klient (`scope`) */
  maxAccess: ApiTokenAccess;
  /** adres serwera MCP, do którego klient dostanie dostęp */
  resource: string;
}

/** Wynik „Zezwól” / „Odmów”: SPA przechodzi na ten adres (adres powrotu klienta z kodem albo błędem). */
export interface OauthRedirect {
  redirectTo: string;
}

export const OAUTH_MESSAGES = {
  clientUnknown: 'Nieznana aplikacja. Połącz konektor ponownie w Claude.',
  redirectMissing: 'Brak adresu powrotu aplikacji.',
  redirectMismatch: 'Adres powrotu nie zgadza się z zarejestrowanym. Połącz konektor ponownie.',
  redirectInvalid: 'Nieprawidłowy adres powrotu',
  redirectFragment: 'Adres powrotu nie może zawierać fragmentu (#)',
  redirectScheme: 'Adres powrotu musi używać https (albo http://localhost / 127.0.0.1 dla aplikacji lokalnych)',
  responseType: 'Obsługujemy tylko response_type=code',
  pkceMissing: 'Brak code_challenge (PKCE jest obowiązkowe)',
  pkceMethod: 'Obsługujemy tylko code_challenge_method=S256',
  pkceInvalid: 'Nieprawidłowy code_challenge',
  stateTooLong: 'Parametr state jest za długi',
  resource: 'Ten serwer autoryzuje tylko dostęp do serwera MCP Fravity OS',
  untrustedRedirect: (host: string) =>
    `Nie przekierowujemy automatycznie do ${host}: ta aplikacja nie ma jeszcze żadnej zgody w Fravity OS.`,
  accessAboveRequest: 'Aplikacja prosi tylko o odczyt',
  tooManyConnections: `Masz już ${OAUTH_MAX_ACTIVE_CONNECTIONS} aktywnych połączeń konektorów. Odłącz nieużywane w Ustawienia → Tokeny i agenci.`,
} as const;
